import { useCallback, useRef, useEffect } from 'react';
import { useConnectionState, useToastState } from '../store/useAppStore';
import { debugLogger } from '../utils/debugLogger';

interface UseHeartbeatOptions {
  interval?: number; // Heartbeat interval in milliseconds
  timeout?: number; // Timeout for each heartbeat request
  maxFailures?: number; // Maximum consecutive failures before marking as disconnected
  enabled?: boolean; // Whether heartbeat is enabled
}

export const useHeartbeat = ({
  interval = 30000, // 30 seconds default
  timeout = 8000, // 8 second timeout
  maxFailures = 3, // 3 consecutive failures
  enabled = true
}: UseHeartbeatOptions = {}) => {
  const { isConnected, authId, setConnectionStatus } = useConnectionState();
  const { showToast } = useToastState();

  // Heartbeat state
  const heartbeatInterval = useRef<number | null>(null);
  const consecutiveFailures = useRef(0);
  const lastSuccessfulHeartbeat = useRef<number>(Date.now());
  const isHeartbeatRunning = useRef(false);

  const performHeartbeat = useCallback(async (): Promise<boolean> => {
    if (!authId || !isConnected) {
      return false;
    }

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeout);

      debugLogger.debug('HEARTBEAT', 'performing heartbeat check', { authId: authId.substring(0, 12) });

      const response = await fetch(`${(import.meta as any).env.VITE_BACKEND_URL}/api/whatsapp/${authId}/health`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (response.ok) {
        const data = await response.json();
        
        if (data.success && data.data?.status === 'connected') {
          // Reset failure counter on success
          consecutiveFailures.current = 0;
          lastSuccessfulHeartbeat.current = Date.now();
          
          debugLogger.debug('HEARTBEAT', 'heartbeat successful', { 
            authId: authId.substring(0, 12),
            failures: consecutiveFailures.current
          });
          
          return true;
        }
      }

      // If we get here, the heartbeat failed
      consecutiveFailures.current++;
      
      debugLogger.warn('HEARTBEAT', 'heartbeat failed', { 
        authId: authId.substring(0, 12),
        status: response.status,
        failures: consecutiveFailures.current,
        maxFailures
      });

      // Check if we've exceeded max failures
      if (consecutiveFailures.current >= maxFailures) {
        debugLogger.error('HEARTBEAT', 'max failures reached, marking as disconnected', {
          authId: authId.substring(0, 12),
          failures: consecutiveFailures.current,
          maxFailures,
          lastSuccess: new Date(lastSuccessfulHeartbeat.current).toISOString()
        });

        // Mark as disconnected
        setConnectionStatus(false);
        
        showToast({
          type: 'error',
          title: 'Conexão Perdida',
          message: 'A conexão com WhatsApp foi perdida. Reconecte para continuar.',
          duration: 8000,
          action: {
            label: 'Reconectar',
            onClick: () => {
              window.location.reload();
            }
          }
        });

        return false;
      }

      return false;
    } catch (error) {
      consecutiveFailures.current++;
      
      const errorMessage = (error as Error).message;
      
      // Don't count timeout/network errors as heavily as actual failures
      if (errorMessage.includes('aborted') || errorMessage.includes('NetworkError')) {
        debugLogger.debug('HEARTBEAT', 'network/timeout error', { 
          error: errorMessage,
          failures: consecutiveFailures.current
        });
      } else {
        debugLogger.warn('HEARTBEAT', 'unexpected error', { 
          error: errorMessage,
          failures: consecutiveFailures.current
        });
      }

      // Still check max failures for any error type
      if (consecutiveFailures.current >= maxFailures) {
        debugLogger.error('HEARTBEAT', 'max failures reached due to errors', {
          authId: authId.substring(0, 12),
          failures: consecutiveFailures.current,
          lastError: errorMessage
        });

        setConnectionStatus(false);
        
        showToast({
          type: 'warning',
          title: 'Problemas de Conexão',
          message: 'Detectados problemas na conexão. Verifique sua internet e reconecte.',
          duration: 8000,
          action: {
            label: 'Reconectar',
            onClick: () => {
              window.location.reload();
            }
          }
        });

        return false;
      }

      return false;
    }
  }, [authId, isConnected, timeout, maxFailures, setConnectionStatus, showToast]);

  const startHeartbeat = useCallback(() => {
    if (isHeartbeatRunning.current || !enabled || !authId || !isConnected) {
      return;
    }

    debugLogger.info('HEARTBEAT', 'starting heartbeat monitoring', { 
      authId: authId.substring(0, 12),
      interval,
      timeout,
      maxFailures
    });

    isHeartbeatRunning.current = true;
    consecutiveFailures.current = 0;
    lastSuccessfulHeartbeat.current = Date.now();

    // Initial heartbeat after a delay
    setTimeout(() => {
      if (isHeartbeatRunning.current && isConnected) {
        performHeartbeat();
      }
    }, 5000);

    // Set up heartbeat interval
    heartbeatInterval.current = window.setInterval(async () => {
      if (!isHeartbeatRunning.current || !isConnected) {
        stopHeartbeat();
        return;
      }

      await performHeartbeat();
    }, interval);

  }, [enabled, authId, isConnected, interval, timeout, maxFailures, performHeartbeat]);

  const stopHeartbeat = useCallback(() => {
    if (heartbeatInterval.current) {
      window.clearInterval(heartbeatInterval.current);
      heartbeatInterval.current = null;
    }

    if (isHeartbeatRunning.current) {
      isHeartbeatRunning.current = false;
      debugLogger.info('HEARTBEAT', 'heartbeat monitoring stopped', {
        authId: authId?.substring(0, 12),
        failures: consecutiveFailures.current
      });
    }
  }, [authId]);

  const resetHeartbeat = useCallback(() => {
    stopHeartbeat();
    consecutiveFailures.current = 0;
    lastSuccessfulHeartbeat.current = Date.now();
    
    if (enabled && authId && isConnected) {
      startHeartbeat();
    }
  }, [enabled, authId, isConnected, startHeartbeat, stopHeartbeat]);

  // Auto-start/stop heartbeat based on connection status
  useEffect(() => {
    if (enabled && authId && isConnected && !isHeartbeatRunning.current) {
      startHeartbeat();
    } else if ((!enabled || !authId || !isConnected) && isHeartbeatRunning.current) {
      stopHeartbeat();
    }

    return () => {
      stopHeartbeat();
    };
  }, [enabled, authId, isConnected, startHeartbeat, stopHeartbeat]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopHeartbeat();
    };
  }, [stopHeartbeat]);

  return {
    isHeartbeatRunning: isHeartbeatRunning.current,
    consecutiveFailures: consecutiveFailures.current,
    lastSuccessfulHeartbeat: lastSuccessfulHeartbeat.current,
    startHeartbeat,
    stopHeartbeat,
    resetHeartbeat,
    performHeartbeat
  };
};

export default useHeartbeat;